/* exported Infinator */
const ACTIVATION_DISTANCE_FROM_BOTTOM_OF_GRID_CONTAINER = 1500; // In px units
const FIRST_PAGE_DEFAULT = 1;
const MINIMUM_SCROLL_DISTANCE = 200; // In px units
const SCROLL_EVENT_DELAY = 2000; // Milliseconds
const LOADING_ELEMENT_ID = 'infinatorLoader';
const SCROLL_TO_TOP_BTN_ID = 'infinatorSTP';
const PREVIOUS_PAGE_BTN_ELEMENT_ID = 'infinatorPreviousPage';
const NEXT_PAGE_BTN_ELEMENT_ID = 'infinatorNextPage';
// Cap consecutive all-gated (zero product-item) page skips so reserve/branch holes
// don't stop the scroll forever, but a fully gated tail can't loop unbounded.
const MAX_EMPTY_PAGE_SKIPS = 10;
const DIRECTIONS = {
	PREVIOUS: 'prev',
	NEXT: 'next',
};
const TYPES = {
	SCROLL: 'scroll',
	CLICK: 'click',
};
class Infinator {
	constructor(settings) {
		// The page stated in the url, defaults to 1
		this.currentPage = new URL(window.location.href).searchParams.get('page') ?? FIRST_PAGE_DEFAULT;
		this.previousPage = this.currentPage;
		// Define class settings
		this.settings = {
			type: 'scroll', // Valid values are: 'scroll' or 'click' - The event listener type to load the next page
			productsGridSelector: '.collection-grid', // The container that the new products will be appended (Make sure it contains ONLY product blocks).
			productsGridSectionId: 'main-collection', // The section id that contains the products grid (only this section will be fetched)
			loadingText: 'Loading...', // The loading element text - Mostly should be passed in from the translations
			loadingBtnClassName: '', // Space seperated classes to be added to the loading element
			previousPageButtonText: 'Load Previous Page',
			nextPageButtonText: 'Load next page',
			loadPageButtonClassName: '',
			scrollToTopBtnText: 'Top', // Scroll to top button inner text
			scrollToTopBtnClassName: '', // Scroll to top button classes
			filtersChangeEvent: 'filter:changed', // The custom event dispatched from the 'theme.js' file when a filter is changed,
			paginationSelector: false, // The default pagination element to remove (pass false to leave the pagination element)
			activationOffsetBottom: ACTIVATION_DISTANCE_FROM_BOTTOM_OF_GRID_CONTAINER, // This distance in px from the bottom of the products container that activates next page render
			beforeEach: function () { /* Default empty funciton */ }, // A function that will be invoked before every page render
			afterEach: function () { /* Default empty funciton */ }, // A callback function that will be invoked after the products has been added to the DOM
			...settings, // Overides all the default settings
		};
		// Bind all methods
		this.bindAllMethods.call(this);
		// Re-initializes the class on filter change (restart from scratch to avoid bugs) -
		// registered even if there's no grid yet, so a filter change that produces one still revives infinite scroll.
		document.addEventListener(this.settings.filtersChangeEvent, this.reinitialize);
		const productsGrid = document.querySelector(this.settings.productsGridSelector);
		if (!productsGrid) {
			return;
		}
		// The distance from the top of the page to the top of the products grid container in units of px
		// This distance will be reducted in the scroll calculation to eliminate header and unrelated layout effects.
		this.containerOffsetHeight = productsGrid.getBoundingClientRect().top;
		// This let us know the height of a typical container for previous page loading scroll
		this.baseContainerHeight = productsGrid.offsetHeight;
		// Defaults to the current url
		this.requestedPageUrl = window.location.href;
		// Initialize the infinite scroll
		this.init();
	}

	bindAllMethods() {
		this.init = this.init.bind(this);
		this.scrollUserToLastViewdProduct = this.scrollUserToLastViewdProduct.bind(this);
		this.setPaginationEventListener = this.setPaginationEventListener.bind(this);
		this.handlePaginationRemoval = this.handlePaginationRemoval.bind(this);
		this.updateContainerHeight = this.updateContainerHeight.bind(this);
		this.createScrollToTopBtn = this.createScrollToTopBtn.bind(this);
		this.showScrollToTopBtn = this.showScrollToTopBtn.bind(this);
		this.hideScrollToTopBtn = this.hideScrollToTopBtn.bind(this);
		this.createLoadNextPageButton = this.createLoadNextPageButton.bind(this);
		this.setReversePagination = this.setReversePagination.bind(this);
		this.prepareProductsTrackingData = this.prepareProductsTrackingData.bind(this);
		this.setProductEventListener = this.setProductEventListener.bind(this);
		this.setInitialHistory = this.setInitialHistory.bind(this);
		this.createLoadingElement = this.createLoadingElement.bind(this);
		this.handleUserScroll = this.handleUserScroll.bind(this);
		this.initiatePageLoadSequence = this.initiatePageLoadSequence.bind(this);
		this.isScrolledToThreshold = this.isScrolledToThreshold.bind(this);
		this.setLoadingState = this.setLoadingState.bind(this);
		this.updateUrl = this.updateUrl.bind(this);
		this.renderRequestedPage = this.renderRequestedPage.bind(this);
		this.addNewProductsToDOM = this.addNewProductsToDOM.bind(this);
		this.getRequestedPageHTML = this.getRequestedPageHTML.bind(this);
		this.getNewUrlQueryParams = this.getNewUrlQueryParams.bind(this);
		this.destroy = this.destroy.bind(this);
		this.reinitialize = this.reinitialize.bind(this);
		this.isIos = this.isIos.bind(this);
	}

	init() {
		// Called from the constructor and from reinitialize() on filter change - the grid may not exist yet either time.
		if (!document.querySelector(this.settings.productsGridSelector)) {
			return;
		}
		this.scrollUserToLastViewdProduct();
		this.createLoadingElement();
		this.setPaginationEventListener();
		this.handlePaginationRemoval();
		this.updateContainerHeight();
		this.setReversePagination();
		this.prepareProductsTrackingData();
		this.setInitialHistory();
		this.createScrollToTopBtn();
	}

	scrollUserToLastViewdProduct() {
		if (window.sessionStorage.getItem(window.location.pathname + '-product')) {
			document
				.querySelector(
					`[data-infinator-id="${window.sessionStorage.getItem(
						window.location.pathname + '-product'
					)}"]`
				)
				?.scrollIntoView({
					block: 'center',
				});
			window.sessionStorage.removeItem(window.location.pathname + '-product');
			window.sessionStorage.removeItem(window.location.pathname + '-page');
		}
		if (!this.isIos()) {
			history.scrollRestoration = 'manual';
		}
	}

	prepareProductsTrackingData() {
		document.querySelectorAll('[data-infinator-id]:not([tracked])').forEach((product) => {
			product.setAttribute('tracked', '');
			product.setAttribute('data-infinator-page', this.currentPage);
			this.setProductEventListener(product);
		});
	}

	setProductEventListener(product) {
		product.addEventListener('click', (e) => {
			window.sessionStorage.setItem(
				window.location.pathname + '-product',
				e.target.closest('[data-infinator-id]').dataset.infinatorId
			);
			window.sessionStorage.setItem(
				window.location.pathname + '-page',
				e.target.closest('[data-infinator-page]').dataset.infinatorPage
			);
		});
	}

	setInitialHistory() {
		history.replaceState(
			{ page: this.currentPage },
			null,
			this.getNewUrlQueryParams(window.location.href, { page: this.currentPage })
		);
	}

	createScrollToTopBtn() {
		if (!this.settings.scrollToTopBtnText) {
			return;
		}
		const scrollToTopBtn = document.createElement('button');
		scrollToTopBtn.innerText = this.settings.scrollToTopBtnText;
		scrollToTopBtn.id = SCROLL_TO_TOP_BTN_ID;
		scrollToTopBtn.style.cssText =
			'display: flex;align-items: center;justify-content: center;transition: transform .6s;position: fixed; right:25px; bottom: 15%; background: #215733; color: white; z-index: 10;padding: 5px; width: 45px;border-radius: 10rem; transform: translateX(5rem) rotate(360deg); font-size: 20px;';
		if (this.settings.scrollToTopBtnClassName) {
			// Add the custom classes passed in the settings object
			for (const className of this.settings.scrollToTopBtnClassName.split(' ')) {
				scrollToTopBtn.classList.add(className);
			}
		}

		const scrollEvent = () => {
			if (window.scrollY < MINIMUM_SCROLL_DISTANCE) {
				return;
			}
			window.removeEventListener('scroll', scrollEvent);
			this.showScrollToTopBtn();
		};
		window.addEventListener('scroll', scrollEvent);

		const clickEvent = () => {
			window.removeEventListener('scroll', scrollEvent);
			window.scrollTo({
				top: 0,
				behavior: 'smooth',
			});
			this.hideScrollToTopBtn();
			setTimeout(() => {
				window.addEventListener('scroll', scrollEvent);
			}, SCROLL_EVENT_DELAY);
		};
		scrollToTopBtn.addEventListener('click', clickEvent);

		// Appends after the container (not inside) so it will always be at the bottom
		document.body.append(scrollToTopBtn);
	}

	showScrollToTopBtn() {
		document.getElementById(SCROLL_TO_TOP_BTN_ID).style.transform = 'unset';
	}

	hideScrollToTopBtn() {
		document.getElementById(SCROLL_TO_TOP_BTN_ID).style.transform =
			'translateX(5rem) rotate(360deg)';
	}
	setPaginationEventListener() {
		if (this.settings.type.toLocaleLowerCase() === TYPES.CLICK) {
			this.createLoadNextPageButton();
		} else {
			// Default and fallback behaviour is scroll
			window.addEventListener(TYPES.SCROLL, this.handleUserScroll);
		}
	}

	handlePaginationRemoval() {
		document.querySelector(this.settings.paginationSelector)?.remove();
	}

	// Once the products have been loaded, the height of the container is changed. Update the new height in the class state.
	updateContainerHeight() {
		this.conatinerHeight = document.querySelector(this.settings.productsGridSelector).offsetHeight;
	}

	createLoadNextPageButton() {
		const nextPageBtn = document.createElement('button');
		nextPageBtn.innerText = this.settings.nextPageButtonText;
		nextPageBtn.id = NEXT_PAGE_BTN_ELEMENT_ID;
		nextPageBtn.classList.add('infinator-btn');
		if (this.settings.loadPageButtonClassName) {
			// Add the custom classes passed in the settings object
			for (const className of this.settings.loadPageButtonClassName.split(' ')) {
				nextPageBtn.classList.add(className);
			}
		}
		nextPageBtn.addEventListener(TYPES.CLICK, () => this.initiatePageLoadSequence(DIRECTIONS.NEXT));
		document.querySelector(this.settings.productsGridSelector).after(nextPageBtn);
	}

	setReversePagination() {
		if (Number(this.currentPage) <= FIRST_PAGE_DEFAULT) {
			return;
		}
		const previousPageBtn = document.createElement('button');
		previousPageBtn.innerText = this.settings.previousPageButtonText;
		previousPageBtn.id = PREVIOUS_PAGE_BTN_ELEMENT_ID;
		previousPageBtn.classList.add('infinator-btn');
		if (this.settings.loadPageButtonClassName) {
			// Add the custom classes passed in the settings object
			for (const className of this.settings.loadPageButtonClassName.split(' ')) {
				previousPageBtn.classList.add(className);
			}
		}
		previousPageBtn.addEventListener('click', () =>
			this.initiatePageLoadSequence(DIRECTIONS.PREVIOUS)
		);
		document.querySelector(this.settings.productsGridSelector).before(previousPageBtn);
	}

	createLoadingElement() {
		const loadingEl = document.createElement('h4');
		loadingEl.innerText = this.settings.loadingText;
		loadingEl.id = LOADING_ELEMENT_ID;
		loadingEl.style.textAlign = 'center';
		loadingEl.style.display = 'none'; // Starts invisible, will be shown on next page load
		if (this.settings.loadingBtnClassName) {
			// Add the custom classes passed in the settings object
			for (const className of this.settings.loadingBtnClassName.split(' ')) {
				loadingEl.classList.add(className);
			}
		}
		// Appends after the container (not inside) so it will always be at the bottom
		document.querySelector(this.settings.productsGridSelector).after(loadingEl);
	}

	handleUserScroll() {
		try {
			// Check if the user is scrolled enough to trigger next page load and prevent next page load if previous is still loading
			if (!this.isScrolledToThreshold() || this.isLoading) {
				return false;
			}
			this.initiatePageLoadSequence(DIRECTIONS.NEXT);
		} catch (e) {
			console.warn(e);
		}
	}

	async initiatePageLoadSequence(direction) {
		this.setLoadingState(true);
		this.settings.beforeEach();
		this.updateUrl(direction);
		const isNextPageReloaded = await this.renderRequestedPage(direction);
		if (isNextPageReloaded) {
			this.updateContainerHeight();
			this.settings.afterEach();
		}
		this.setLoadingState(false);
	}

	// Check if the user is scrolled at least 'ACTIVATION_DISTANCE_FROM_BOTTOM_OF_GRID_CONTAINER'px from the bottom of the products container
	isScrolledToThreshold() {
		// Subtract the containerOffsetHeight to eliminate header and external layout impact.
		const ContainerBottomAbsolutePosition = this.conatinerHeight - this.containerOffsetHeight;
		const scrollDistanceFromContainerBottom = ContainerBottomAbsolutePosition - window.scrollY;
		return scrollDistanceFromContainerBottom < this.settings.activationOffsetBottom;
	}

	setLoadingState(isLoading) {
		this.isLoading = isLoading; // Important because the next scroll happens before the products loaded so the handle function will run too many times
		// Show or hide the loding element based on the loading state
		const loadingElement = document.getElementById(LOADING_ELEMENT_ID);
		if (!loadingElement) {
			// Prevent error if the loading element is not in the DOM for some reason, it's not important enough to terminate the operation.
			return;
		}
		return (loadingElement.style.display = isLoading ? 'block' : 'none');
	}

	// Keep track of the page state in the URL
	updateUrl(direction) {
		// Make sure number addition rather than string concatination
		let requestedPageNumber;
		if (direction === DIRECTIONS.NEXT) {
			// eslint-disable-next-line no-magic-numbers
			this.currentPage = Number(this.currentPage) + 1;
			requestedPageNumber = this.currentPage;
		} else {
			// eslint-disable-next-line no-magic-numbers
			this.previousPage = Number(this.previousPage) - 1;
			requestedPageNumber = this.previousPage;
		}
		this.requestedPageUrl = this.getNewUrlQueryParams(window.location.href, {
			page: requestedPageNumber,
		});
		// Use replaceState so if the users clicks back he wouldn't have to go through all the previously loaded pages
		history.replaceState({ page: requestedPageNumber }, null, this.requestedPageUrl);
	}

	getProductNodes(productsContainer) {
		if (!productsContainer) {
			return [];
		}
		return Array.from(productsContainer.children).filter((el) =>
			el.matches('product-item, [data-infinator-id]')
		);
	}

	hasMorePagesInDirection(requestedPageDOM, direction) {
		const rel = direction === DIRECTIONS.NEXT ? 'next' : 'prev';
		return Boolean(requestedPageDOM.querySelector(`page-pagination a[rel="${rel}"]`));
	}

	async renderRequestedPage(direction, emptySkips = 0) {
		// Get the URL with all the query params of the requested page to fetch and display it.
		const endpoint = this.getNewUrlQueryParams(this.requestedPageUrl, {
			// eslint-disable-next-line camelcase
			section_id: this.settings.productsGridSectionId,
		});
		const requestedPageDOM = await this.getRequestedPageHTML(endpoint);

		const newProducts = requestedPageDOM.querySelector(this.settings.productsGridSelector);
		const productNodes = this.getProductNodes(newProducts);

		if (productNodes.length === 0) {
			// All products on this Shopify page were gated (branch/reserve) — or the
			// grid is truly empty. Skip ahead while pagination still has pages, so a
			// middle all-gated page does not permanently stop infinite scroll.
			if (
				emptySkips < MAX_EMPTY_PAGE_SKIPS &&
				this.hasMorePagesInDirection(requestedPageDOM, direction)
			) {
				this.updateUrl(direction);
				return this.renderRequestedPage(direction, emptySkips + 1);
			}
			// eslint-disable-next-line no-magic-numbers
			return this.destroy(Number(this.currentPage) - emptySkips - 1);
		}

		if (Number(this.previousPage) === FIRST_PAGE_DEFAULT) {
			// No need for previous page button
			document.getElementById(PREVIOUS_PAGE_BTN_ELEMENT_ID)?.remove();
		}
		// Append only product cards — never empty-state or other non-product children
		this.addNewProductsToDOM(productNodes, direction);
		return true;
	}

	addNewProductsToDOM(productNodes, direction) {
		const productsContainer = document.querySelector(this.settings.productsGridSelector);

		if (direction === DIRECTIONS.NEXT) {
			for (let i = 0; i < productNodes.length; i++) {
				const newProduct = productNodes[i].cloneNode(true);
				newProduct.dataset.infinatorPage = this.currentPage;
				productsContainer.appendChild(newProduct);
				this.setProductEventListener(newProduct);
			}
		} else {
			// Reverse order
			// eslint-disable-next-line no-magic-numbers
			for (let i = productNodes.length - 1; i >= 0; i--) {
				const newProduct = productNodes[i].cloneNode(true);
				newProduct.dataset.infinatorPage = this.previousPage;
				productsContainer.prepend(newProduct);
				this.setProductEventListener(newProduct);
			}
			window.scrollTo({
				top: this.baseContainerHeight + window.scrollY,
			});
		}
	}

	async getRequestedPageHTML(requestedPageUrl) {
		const response = await fetch(requestedPageUrl);
		const requestedPageHTMLString = await response.text();
		return new DOMParser().parseFromString(requestedPageHTMLString, 'text/html');
	}

	// All of the pagination, filters and sort logic happens in the URL. Keep it clean and updated.
	getNewUrlQueryParams(previousUrl, params) {
		const url = new URL(previousUrl);
		const urlSearchParams = url.searchParams;
		for (const [key, value] of Object.entries(params)) {
			urlSearchParams.set(key, value); // Use set to override the param if it's already set
		}
		if (Number(urlSearchParams.get('page')) === FIRST_PAGE_DEFAULT) {
			urlSearchParams.delete('page');
		}
		return url.toString();
	}

	// Remove everything the Infinator has added to the DOM
	destroy(page = null) {
		window.removeEventListener('scroll', this.handleUserScroll);
		// Reset current page and the URL
		this.currentPage = page ?? FIRST_PAGE_DEFAULT;
		this.setInitialHistory();
		// Remove the added loading elements if still in the DOM
		document.getElementById(LOADING_ELEMENT_ID)?.remove();
		document.getElementById(NEXT_PAGE_BTN_ELEMENT_ID)?.remove();
		if (Number(this.previousPage) === FIRST_PAGE_DEFAULT) {
			document.getElementById(PREVIOUS_PAGE_BTN_ELEMENT_ID)?.remove();
		}
	}

	reinitialize() {
		this.destroy();
		this.init();
	}

	isIos() {
		return (
			['iPad Simulator', 'iPhone Simulator', 'iPad', 'iPhone'].includes(navigator.platform) ||
			// IPad on iOS 13 detection
			(navigator.userAgent.includes('Mac') && 'ontouchend' in document)
		);
	}
}